import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  Avatar,
  Tab,
  Tabs,
  Alert,
  IconButton,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FlashOn,
  Schedule,
  Favorite,
  FavoriteBorder,
  Flight,
  Restaurant,
  ShoppingCart,
  LocalGasStation,
  Star,
  ArrowForward,
  AccessTime,
  TrendingUp,
  Bookmark,
  BookmarkBorder,
  Refresh,
  Info,
} from '@mui/icons-material';
import { getBrandLogo } from '../services/brandLogos';
import realDealsService, { 
  CreditCardDeal, 
  CashbackDeal, 
  FlashDeal, 
  PersonalizedDeal 
} from '../services/realDealsService';
import { useAuth } from '../contexts/AuthContext';
import { realDataService } from '../services/realDataService';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));

const FlashDealCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -20,
    right: -20,
    width: 80,
    height: 80,
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
  },
}));

const PremiumDealCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));

const CategoryCard = styled(Card)<{ category: string }>(({ theme, category }) => ({
  borderRadius: 12,
  background: 
    category === 'travel' ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' :
    category === 'dining' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
    category === 'shopping' ? 'linear-gradient(135deg, #10b981 0%, #047857 100%)' :
    'linear-gradient(135deg, #6b7280 0%, #374151 100%)',
  color: 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const Deals: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [bookmarked, setBookmarked] = useState<Set<string>>(new Set());
  const { user } = useAuth();

  // Real deals state
  const [flashDeals, setFlashDeals] = useState<FlashDeal[]>([]);
  const [creditCardDeals, setCreditCardDeals] = useState<CreditCardDeal[]>([]);
  const [cashbackDeals, setCashbackDeals] = useState<CashbackDeal[]>([]);
  const [personalizedDeals, setPersonalizedDeals] = useState<PersonalizedDeal[]>([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Configuration status
  const configStatus = realDealsService.getConfigurationStatus();

  // Load all deals on mount
  useEffect(() => {
    loadAllDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAllDeals = async () => {
    console.log('🔄 Loading real deals...');
    setLoading(true);
    setError(null);

    try {
      // Load user's programs for personalized deals
      let userPrograms;
      if (user) {
        try {
          userPrograms = await realDataService.getLoyaltyPrograms(user.id);
        } catch (err) {
          console.warn('Could not load user programs:', err);
        }
      }

      // Load all deals in parallel
      const [flash, creditCards, cashback, personalized] = await Promise.all([
        realDealsService.getFlashDeals(),
        realDealsService.getCreditCardDeals(),
        realDealsService.getCashbackDeals(),
        realDealsService.getPersonalizedDeals(userPrograms),
      ]);

      setFlashDeals(flash);
      setCreditCardDeals(creditCards);
      setCashbackDeals(cashback);
      setPersonalizedDeals(personalized);

      console.log('✅ Deals loaded:', {
        flash: flash.length,
        creditCards: creditCards.length,
        cashback: cashback.length,
        personalized: personalized.length,
      });
    } catch (err) {
      console.error('❌ Error loading deals:', err);
      setError('Failed to load deals. Using fallback data.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    realDealsService.clearCache();
    loadAllDeals();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const toggleFavorite = (dealId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(dealId)) {
        newFavorites.delete(dealId);
      } else {
        newFavorites.add(dealId);
      }
      return newFavorites;
    });
  };

  const toggleBookmark = (dealId: string) => {
    setBookmarked(prev => {
      const newBookmarked = new Set(prev);
      if (newBookmarked.has(dealId)) {
        newBookmarked.delete(dealId);
      } else {
        newBookmarked.add(dealId);
      }
      return newBookmarked;
    });
  };

  // Featured deals derived from credit card deals
  const featuredDeals = creditCardDeals.slice(0, 3).map(deal => ({
    id: deal.id,
    title: deal.cardName,
    description: deal.bonusValue,
    brand: deal.issuer,
    value: deal.bonusValue,
    validUntil: deal.expiresAt?.toLocaleDateString() || 'Limited Time',
    category: deal.category.toLowerCase(),
    terms: deal.spendRequirementText,
    estimatedValue: deal.estimatedValue,
    source: deal.source, // Preserve source field for badge display
  }));

  // Category deals derived from cashback deals
  const categoryDeals = [
    {
      category: 'travel',
      title: 'Travel Rewards',
      icon: <Flight />,
      deals: cashbackDeals.filter(d => d.category.toLowerCase().includes('travel')).length || 12,
      topOffer: 'Up to 5X miles',
    },
    {
      category: 'dining',
      title: 'Dining Offers',
      icon: <Restaurant />,
      deals: cashbackDeals.filter(d => d.category.toLowerCase().includes('dining') || d.category.toLowerCase().includes('restaurant')).length || 8,
      topOffer: '25% cashback',
    },
    {
      category: 'shopping',
      title: 'Shopping Deals',
      icon: <ShoppingCart />,
      deals: cashbackDeals.filter(d => d.category.toLowerCase().includes('shopping')).length || 15,
      topOffer: '4X points',
    },
    {
      category: 'fuel',
      title: 'Fuel Savings',
      icon: <LocalGasStation />,
      deals: 6,
      topOffer: '₹5/L discount',
    },
  ];

  // Limited time offers from credit cards
  const limitedTimeOffers = creditCardDeals
    .filter(deal => deal.expiresAt && deal.expiresAt > new Date())
    .slice(0, 2)
    .map((deal, idx) => ({
      id: deal.id,
      title: deal.cardName,
      description: deal.spendRequirementText,
      brand: deal.issuer,
      multiplier: deal.bonusPoints > 50000 ? '10X' : '5X',
      bonus: undefined,
      expires: deal.expiresAt?.toISOString().split('T')[0] || '2024-12-31',
      claimed: 45 + idx * 22,
      total: 100 + idx * 50,
      source: deal.source, // Preserve source field for badge display
    }));

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Deals & Offers
          </Typography>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Refresh
          </Button>
        </Box>

        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
          Discover exclusive deals and maximize your rewards potential
        </Typography>

        {/* API Configuration Status */}
        {configStatus.anyConfigured && (
          <Alert 
            severity="success" 
            icon={<FlashOn />}
            sx={{ 
              mb: 2,
              borderRadius: 3,
              '& .MuiAlert-icon': {
                color: '#10b981',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ✅ Live deals active! Fetching real deals from Doctor of Credit, The Points Guy, and other FREE sources.
            </Typography>
          </Alert>
        )}

        {/* Error handling */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Flash sale alert */}
        {flashDeals.length > 0 && (
          <Alert 
            severity="info" 
            icon={<FlashOn />}
            sx={{ 
              mb: 2,
              borderRadius: 3,
              '& .MuiAlert-icon': {
                color: '#f59e0b',
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              🔥 {flashDeals.length} Flash {flashDeals.length === 1 ? 'Deal' : 'Deals'} Active! Limited time offers ending soon
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Flash Deals */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlashOn sx={{ color: '#ef4444' }} />
          Flash Deals
        </Typography>
        
        {loading ? (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3 
          }}>
            {[1, 2].map((n) => (
              <Card key={n} sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2, borderRadius: 2 }} />
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : flashDeals.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            No flash deals available right now. Check back soon!
          </Alert>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3 
          }}>
            {flashDeals.map((deal) => (
              <FlashDealCard key={deal.id}>
                <CardContent sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {deal.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {deal.description}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => toggleBookmark(deal.id)}
                      sx={{ color: 'white' }}
                    >
                      {bookmarked.has(deal.id) ? <Bookmark /> : <BookmarkBorder />}
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Chip 
                      label={deal.brand} 
                      sx={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                        color: 'white',
                        fontWeight: 600,
                      }} 
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime sx={{ fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {deal.timeLeft}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {deal.multiplier || deal.bonus || deal.value}
                      </Typography>
                      {deal.originalRate && (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          was {deal.originalRate}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        },
                      }}
                    >
                      Activate
                    </Button>
                  </Box>

                  {/* Source indicator */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <Chip 
                      label={deal.source === 'mock' ? 'Demo Data' : 'Live Deal'}
                      size="small"
                      sx={{ 
                        backgroundColor: deal.source === 'mock' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(16, 185, 129, 0.3)',
                        color: 'white',
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                </CardContent>
              </FlashDealCard>
            ))}
          </Box>
        )}
      </Box>

      {/* Category Navigation */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          Browse by Category
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2 
        }}>
          {categoryDeals.map((category) => (
            <CategoryCard key={category.category} category={category.category}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Box sx={{ mb: 2 }}>
                  {category.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {category.title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  {category.deals} deals
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {category.topOffer}
                </Typography>
              </CardContent>
            </CategoryCard>
          ))}
        </Box>
      </Box>

      {/* Tabs for different deal types */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Featured Deals" />
          <Tab label="Cashback Deals" />
          <Tab label="For You" />
          <Tab label="Limited Time" />
        </Tabs>
      </Box>

      {/* Featured Deals Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 3 
        }}>
          {featuredDeals.map((deal) => {
            const brandLogo = getBrandLogo(deal.brand.toLowerCase().replace(/\s+/g, ''));
            return (
              <StyledCard key={deal.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={brandLogo.logo}
                        sx={{ 
                          width: 40, 
                          height: 40,
                          bgcolor: 'primary.main',
                        }}
                      >
                        {brandLogo.fallbackIcon}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {deal.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {deal.brand}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={() => toggleFavorite(deal.id)}
                      size="small"
                    >
                      {favorites.has(deal.id) ? 
                        <Favorite sx={{ color: '#ef4444' }} /> : 
                        <FavoriteBorder />
                      }
                    </IconButton>
                  </Box>

                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    {deal.description}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        {deal.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Est. value: {deal.estimatedValue}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`Until ${deal.validUntil}`}
                      size="small"
                      icon={<Schedule />}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                    {deal.terms}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForward />}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      Claim Deal
                    </Button>
                  </Box>

                  {/* Source indicator */}
                  <Chip 
                    label={deal.source === 'mock' ? 'Demo Data' : 'Live Deal'}
                    size="small"
                    sx={{ 
                      backgroundColor: deal.source === 'mock' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(16, 185, 129, 0.1)',
                      color: deal.source === 'mock' ? 'text.secondary' : '#10b981',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  />
                </CardContent>
              </StyledCard>
            );
          })}
        </Box>
      </TabPanel>

      {/* Cashback Deals Tab */}
      <TabPanel value={selectedTab} index={1}>
        {cashbackDeals.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            No cashback deals available right now. Check back soon!
          </Alert>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3 
          }}>
            {cashbackDeals.map((deal) => {
              const brandLogo = getBrandLogo(deal.merchant.toLowerCase().replace(/\s+/g, ''));
              return (
                <StyledCard key={deal.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={brandLogo.logo}
                          sx={{ 
                            width: 40, 
                            height: 40,
                            bgcolor: 'primary.main',
                          }}
                        >
                          {brandLogo.fallbackIcon}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {deal.merchant}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {deal.category}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        onClick={() => toggleFavorite(deal.id)}
                        size="small"
                      >
                        {favorites.has(deal.id) ? 
                          <Favorite sx={{ color: '#ef4444' }} /> : 
                          <FavoriteBorder />
                        }
                      </IconButton>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {deal.description}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                          {deal.cashbackRate}
                        </Typography>
                        {deal.cashbackAmount && (
                          <Typography variant="caption" color="text.secondary">
                            {deal.cashbackAmount}
                          </Typography>
                        )}
                      </Box>
                      {deal.expiresAt && (
                        <Chip 
                          label={`Until ${deal.expiresAt.toLocaleDateString()}`}
                          size="small"
                          icon={<Schedule />}
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        endIcon={<ArrowForward />}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        onClick={() => deal.url && window.open(deal.url, '_blank')}
                      >
                        Get Cashback
                      </Button>

                      {/* Source indicator */}
                      <Chip 
                        label={deal.source === 'mock' ? 'Demo Data' : 'Live Deal'}
                        size="small"
                        sx={{ 
                          backgroundColor: deal.source === 'mock' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(16, 185, 129, 0.1)',
                          color: deal.source === 'mock' ? 'text.secondary' : '#10b981',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </CardContent>
                </StyledCard>
              );
            })}
          </Box>
        )}
      </TabPanel>

      {/* Personalized Deals Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 3 
        }}>
          {personalizedDeals.map((deal) => {
            const brandLogo = getBrandLogo(deal.brand.toLowerCase().replace(/\s+/g, ''));
            return (
              <StyledCard key={deal.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={brandLogo.logo}
                        sx={{ 
                          width: 40, 
                          height: 40,
                          bgcolor: 'primary.main',
                        }}
                      >
                        {brandLogo.fallbackIcon}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {deal.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {deal.brand}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Star sx={{ color: '#f59e0b', fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {deal.matchScore}%
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    {deal.description}
                  </Typography>

                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                    {deal.offer}
                  </Typography>

                  <Alert 
                    severity="info" 
                    icon={<TrendingUp />}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="caption">
                      {deal.reason}
                    </Typography>
                  </Alert>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForward />}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      Get Personalized Deal
                    </Button>
                  </Box>

                  {/* Source indicator */}
                  <Chip 
                    label={deal.source === 'mock' ? 'Demo Data' : 'Live Deal'}
                    size="small"
                    sx={{ 
                      backgroundColor: deal.source === 'mock' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(16, 185, 129, 0.1)',
                      color: deal.source === 'mock' ? 'text.secondary' : '#10b981',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  />
                </CardContent>
              </StyledCard>
            );
          })}
        </Box>
      </TabPanel>

      {/* Limited Time Offers Tab */}
      <TabPanel value={selectedTab} index={3}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3 
        }}>
          {limitedTimeOffers.map((offer) => (
            <PremiumDealCard key={offer.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {offer.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {offer.description}
                    </Typography>
                  </Box>
                  <Chip 
                    label="LIMITED"
                    size="small"
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                    {offer.multiplier || offer.bonus}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {offer.brand} • Expires {offer.expires}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">
                      {offer.claimed} of {offer.total} claimed
                    </Typography>
                    <Typography variant="body2">
                      {Math.round((offer.claimed / offer.total) * 100)}%
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    height: 6, 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}>
                    <Box sx={{ 
                      height: '100%', 
                      width: `${(offer.claimed / offer.total) * 100}%`,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: 3,
                      transition: 'width 0.3s ease',
                    }} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForward />}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    Claim Now
                  </Button>
                  
                  {/* Source indicator */}
                  <Chip 
                    label={offer.source === 'mock' ? 'Demo Data' : 'Live Deal'}
                    size="small"
                    sx={{ 
                      backgroundColor: offer.source === 'mock' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(16, 185, 129, 0.3)',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </CardContent>
            </PremiumDealCard>
          ))}
        </Box>
      </TabPanel>
    </Container>
  );
};